import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { fileService } from '../services/fileService';
import { folderService } from '../services/folderService';
import ShareModal from '../components/ShareModal';
import VersionHistoryModal from '../components/VersionHistoryModal';
import FilePreviewModal from '../components/FilePreviewModal';
import {
  Upload,
  Download,
  Trash2,
  File,
  Folder,
  FolderPlus,
  Search,
  AlertCircle,
  CheckCircle,
  X,
  ChevronRight,
  Home,
  Share2,
Clock,
  Eye,
  CheckSquare,
  Square
} from 'lucide-react';

const Files = () => {
  const { user } = useAuth();
  const [allFiles, setAllFiles] = useState([]);
  const [allFolders, setAllFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [fileToShare, setFileToShare] = useState(null);
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
  const [fileForVersions, setFileForVersions] = useState(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [fileForPreview, setFileForPreview] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Create folder modal
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [filesData, foldersData] = await Promise.all([
        fileService.listFiles(),
        folderService.listFolders()
      ]);
      console.log('Loaded files:', filesData);
      console.log('Loaded folders:', foldersData);
      setAllFiles(filesData);
      setAllFolders(foldersData);
    } catch (err) {
      setError('Failed to load files');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filter files and folders based on current location
  const displayedFiles = searchQuery 
    ? allFiles.filter(f => f.filename.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          f.original_filename.toLowerCase().includes(searchQuery.toLowerCase()))
    : allFiles.filter(f => {
        // Handle null folder_id comparison properly
        if (currentFolder === null) {
          return f.folder_id === null || f.folder_id === undefined;
        }
        return f.folder_id === currentFolder;
      });
    
  const displayedFolders = searchQuery 
    ? [] 
    : allFolders.filter(f => f.parent_id === currentFolder);

  console.log('Current folder:', currentFolder);
  console.log('All files:', allFiles);
  console.log('Displayed files:', displayedFiles);
  console.log('Displayed folders:', displayedFolders);

  // Get breadcrumb path
  const getBreadcrumbs = () => {
    if (!currentFolder) return [];
    const path = [];
    let folder = allFolders.find(f => f.id === currentFolder);
    while (folder) {
      path.unshift(folder);
      folder = allFolders.find(f => f.id === folder.parent_id);
    }
    return path;
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setUploadFiles(files);
      setShowUploadModal(true);
    }
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;

    setUploading(true);
    setError('');
    let uploaded = 0;
    for (const file of uploadFiles) {
      try {
        await fileService.uploadFile(file, currentFolder);
        uploaded++;
      } catch (err) {
        setError(`Failed to upload ${file.name}: ${err.response?.data?.detail || err.message}`);
      }
    }
    setUploading(false);
    if (uploaded > 0) {
      setSuccess(`Uploaded ${uploaded} file${uploaded > 1 ? 's' : ''} successfully!`);
      setShowUploadModal(false);
      setUploadFiles([]);
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    }
  };


  const openShareModal = (file) => {
    setFileToShare(file);
    setIsShareModalOpen(true);
  };

  const handleShareFile = async (shareData) => {
    try {
      await fileService.shareFile(fileToShare.id, shareData);
      setError('');
      // Could add a success message here
    } catch (err) {
      setError('Failed to share file: ' + (err.response?.data?.detail || err.message));
      throw err;
    }
  };


  const openVersionModal = (file) => {
    setFileForVersions(file);
    setIsVersionModalOpen(true);
  };



  const openPreviewModal = (file) => {
    // Only preview images and PDFs
    const canPreview = file.mime_type?.startsWith('image/') || file.mime_type === 'application/pdf';
    if (canPreview) {
      setFileForPreview(file);
      setIsPreviewModalOpen(true);
    } else {
      // Just download if can't preview
      handleDownload(file);
    }
  };


  // Bulk selection handlers
  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedFiles.length === displayedFiles.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(displayedFiles.map(f => f.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedFiles.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''}?`)) {
      return;
    }

    setBulkActionLoading(true);
    setError('');
    
    let successCount = 0;
    let failCount = 0;

    for (const fileId of selectedFiles) {
      try {
        await fileService.deleteFile(fileId);
        successCount++;
      } catch (err) {
        failCount++;
      }
    }

    setBulkActionLoading(false);
    setSelectedFiles([]);
    
    if (successCount > 0) {
      setSuccess(`Successfully deleted ${successCount} file${successCount > 1 ? 's' : ''}`);
      setTimeout(() => setSuccess(''), 3000);
    }
    
    if (failCount > 0) {
      setError(`Failed to delete ${failCount} file${failCount > 1 ? 's' : ''}`);
    }

    await loadData();
  };

  const clearSelection = () => {
    setSelectedFiles([]);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if leaving the main container
    if (e.target.className && e.target.className.includes('drag-drop-zone')) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    setUploading(true);
    setError('');

    // Upload each file
    for (const file of files) {
      try {
        await fileService.uploadFile(file, currentFolder);
        setSuccess(`Uploaded ${file.name} successfully!`);
      } catch (err) {
        setError(`Failed to upload ${file.name}: ${err.response?.data?.detail || err.message}`);
      }
    }

    setUploading(false);
    await loadData();
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDownload = async (file) => {
    try {
      await fileService.downloadFile(file.id, file.filename);
      setSuccess('Download started!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to download file');
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }
    
    try {
      await fileService.deleteFile(fileId);
      setSuccess('File deleted successfully!');
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete file');
    }
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    setCreatingFolder(true);
    setError('');
    
    try {
      await folderService.createFolder(newFolderName, currentFolder);
      setSuccess('Folder created successfully!');
      setShowFolderModal(false);
      setNewFolderName('');
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <div 
      className="max-w-7xl mx-auto drag-drop-zone relative"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag and Drop Overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-virgo-teal-600 bg-opacity-90 z-50 flex items-center justify-center">
          <div className="bg-virgo-navy-700 rounded-lg p-12 shadow-2xl text-center">
            <Upload className="w-20 h-20 text-virgo-teal-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-virgo-gold-400 mb-2">Drop files here</h2>
            <p className="text-virgo-teal-300">Release to upload to {currentFolder ? 'current folder' : 'root'}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-virgo-teal-100">My Files</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowFolderModal(true)}
            className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
          >
            <FolderPlus size={20} />
            <span>New Folder</span>
          </button>
          <label className="flex items-center space-x-2 bg-virgo-teal-600 text-white px-4 py-2 rounded-lg hover:bg-virgo-teal-700 transition cursor-pointer">
            <Upload size={20} />
            <span>Upload File</span>
            <input
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              multiple
            />
          </label>
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      <div className="mb-4 flex items-center space-x-2 text-sm">
        <button
          onClick={() => setCurrentFolder(null)}
          className="flex items-center text-virgo-teal-400 hover:text-virgo-teal-200"
        >
          <Home size={16} className="mr-1" />
          <span>Home</span>
        </button>
        {breadcrumbs.map((folder, index) => (
          <React.Fragment key={folder.id}>
            <ChevronRight size={16} className="text-virgo-teal-500" />
            <button
              onClick={() => setCurrentFolder(folder.id)}
              className={`text-virgo-teal-400 hover:text-virgo-teal-200 ${
                index === breadcrumbs.length - 1 ? 'font-semibold' : ''
              }`}
            >
              {folder.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex space-x-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 text-virgo-teal-500" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search files..."
              className="w-full pl-10 pr-4 py-2 border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
            />
          </div>
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition"
            >
              Clear
            </button>
          )}
        </div>
      </form>

      {/* Bulk Action Bar */}
      {selectedFiles.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-blue-900">
              {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
            </span>
            <button
              onClick={clearSelection}
              className="text-sm text-virgo-teal-400 hover:text-virgo-teal-200 underline"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkDelete}
              disabled={bulkActionLoading}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 size={16} />
              {bulkActionLoading ? 'Deleting...' : 'Delete Selected'}
            </button>
          </div>
        </div>
      )}


      {/* Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-center">
          <AlertCircle size={20} className="mr-2" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X size={16} />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded flex items-center">
          <CheckCircle size={20} className="mr-2" />
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Files and Folders List */}
      <div className="bg-virgo-navy-700 rounded-lg shadow">
        {loading ? (
          <div className="p-8 text-center text-virgo-teal-300">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-virgo-navy-600 border-b">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedFiles.length === displayedFiles.length && displayedFiles.length > 0}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 text-virgo-teal-400 focus:ring-virgo-teal-500 border-virgo-teal-700 rounded cursor-pointer"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                    Modified
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {/* Folders */}
                {displayedFolders.map((folder) => (
                  <tr
                    key={`folder-${folder.id}`}
                    className="hover:bg-virgo-navy-600 cursor-pointer"
                    onClick={() => setCurrentFolder(folder.id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Folder className="text-virgo-teal-300 mr-3" size={20} />
                        <span className="text-white font-medium">{folder.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                      —
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                      {formatDate(folder.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                      —
                    </td>
                  </tr>
                ))}

                {/* Files */}
                {displayedFiles.map((file) => (
                  <tr key={`file-${file.id}`} className="hover:bg-virgo-navy-600">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedFiles.includes(file.id)}
                        onChange={() => toggleFileSelection(file.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 text-virgo-teal-400 focus:ring-virgo-teal-500 border-virgo-teal-700 rounded cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <File className="text-virgo-teal-500 mr-3" size={20} />
                        <span className="text-white">{file.filename}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                      {formatFileSize(file.file_size)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                      {formatDate(file.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openPreviewModal(file)}
                          className="text-cyan-600 hover:text-cyan-800"
                          title="Preview"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => handleDownload(file)}
                          className="text-virgo-teal-400 hover:text-virgo-teal-200"
                          title="Download"
                        >
                          <Download size={18} />
                        </button>
                        <button
                          onClick={() => openShareModal(file)}
                          className="text-green-600 hover:text-green-800"
                          title="Share"
                        >
                          <Share2 size={18} />
                        </button>
                        <button
                          onClick={() => openVersionModal(file)}
                          className="text-cyan-600 hover:text-cyan-800"
                          title="Version History"
                        >
                          <Clock size={18} />
                        </button>
                        {user?.id === file.owner_id && (
                          <button
                            onClick={() => handleDelete(file.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}

                {displayedFolders.length === 0 && displayedFiles.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-virgo-teal-400">
                      {searchQuery ? 'No files found matching your search.' : 'No files or folders found. Upload your first file!'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-virgo-navy-700 rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-virgo-teal-100">Upload Files</h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFiles([]);
                }}
                className="text-virgo-teal-400 hover:text-virgo-teal-200"
              >
                <X size={24} />
              </button>
            </div>
            
            {uploadFiles.length > 0 && (
              <div className="mb-4 space-y-2 max-h-60 overflow-y-auto">
                {uploadFiles.map((file, idx) => (
                  <div key={idx} className="p-3 bg-virgo-navy-800 rounded flex items-center justify-between">
                    <div className="flex items-center min-w-0">
                      <File className="text-virgo-teal-300 mr-3 flex-shrink-0" size={20} />
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">{file.name}</p>
                        <p className="text-sm text-virgo-teal-400">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <button onClick={() => setUploadFiles(prev => prev.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-300 ml-2 flex-shrink-0">
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <p className="text-xs text-virgo-teal-500">{uploadFiles.length} file{uploadFiles.length > 1 ? 's' : ''} selected</p>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFiles([]);
                }}
                className="flex-1 bg-gray-300 text-virgo-teal-200 px-4 py-2 rounded-lg hover:bg-gray-400 transition"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="flex-1 bg-virgo-teal-600 text-white px-4 py-2 rounded-lg hover:bg-virgo-teal-700 transition disabled:bg-gray-400"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-virgo-navy-700 rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-virgo-teal-100">Create New Folder</h2>
              <button
                onClick={() => {
                  setShowFolderModal(false);
                  setNewFolderName('');
                }}
                className="text-virgo-teal-400 hover:text-virgo-teal-200"
              >
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleCreateFolder}>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Folder name"
                className="w-full px-4 py-2 border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500 mb-4"
                required
                disabled={creatingFolder}
              />

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowFolderModal(false);
                    setNewFolderName('');
                  }}
                  className="flex-1 bg-gray-300 text-virgo-teal-200 px-4 py-2 rounded-lg hover:bg-gray-400 transition"
                  disabled={creatingFolder}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingFolder}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition disabled:bg-gray-400"
                >
                  {creatingFolder ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        onSubmit={handleShareFile}
        file={fileToShare}
      />

      {/* Version History Modal */}
      <VersionHistoryModal
        isOpen={isVersionModalOpen}
        onClose={() => setIsVersionModalOpen(false)}
        file={fileForVersions}
      />

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        file={fileForPreview}
      />
    </div>
  );
};

export default Files;
